/**
 * Portal Manta · GestaoPage
 * Extraído de App.jsx (v29 modular)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { DADOS } from '../../data/lotes-pr.js';
import { V } from '../../globals.js';
import { M } from '../../tokens/colors.js';

const GestaoPage = memo(function GestaoPage_Inner(){
  const lotes=['3','4','5'];

  // ── helpers de extração ───────────────────────────────
  const extensaoKm=lk=>{
    const e=DADOS[lk].extensao||'';
    const m=e.match(/([\d.,]+)/);
    return m?parseFloat(m[1].replace(',','.')):null;
  };
  const tipoQtd=(lk,cod)=>{
    const t=(DADOS[lk].tipologias||[]).find(x=>x.cod===cod);
    return t?.qtd??null;
  };
  const tipoVal=(lk,cod)=>{
    const t=(DADOS[lk].tipologias||[]).find(x=>x.cod===cod);
    return t?.v??null; // R$ milhões
  };
  const cpQtd=(lk,codigo)=>{
    const it=(DADOS[lk].dados_manta?.compara_parametrico?.itens||[]).find(x=>x.codigo===codigo);
    return it?.qtde??null;
  };
  const cpVal=(lk,codigo)=>{
    const it=(DADOS[lk].dados_manta?.compara_parametrico?.itens||[]).find(x=>x.codigo===codigo);
    return it?.pr_vias?.preco_total??null; // R$ (bruto)
  };
  // Retorna CU rateado pró-rata de um sub-item do grupo (mesma lógica da 5.1)
  const cuRateado=(lk,codSub)=>{
    const itens=DADOS[lk].dados_manta?.compara_parametrico?.itens||[];
    const sub=itens.find(x=>x.codigo===codSub);
    if(!sub||sub.qtde==null||sub.qtde===0) return {total:null,cu:null};
    const gc=codSub.split('.').slice(0,3).join('.');
    const pai=itens.find(x=>x.is_group&&x.codigo===gc);
    if(!pai||!pai.pr_vias?.preco_total) return {total:null,cu:null};
    const sibs=itens.filter(x=>!x.is_group&&x.codigo.startsWith(gc+'.'));
    const totQ=sibs.reduce((s,x)=>s+(x.qtde||0),0);
    if(!totQ) return {total:null,cu:null};
    const total=pai.pr_vias.preco_total*(sub.qtde/totQ);
    return {total,cu:total/sub.qtde};
  };

  // CAPEX de implantação = soma das tipologias A1..A13 (R$ milhões)
  const capexImpl=lk=>{
    const tips=DADOS[lk].tipologias||[];
    return tips.filter(t=>/^A\d+$/.test(t.cod)).reduce((s,t)=>s+(t.v||0),0);
  };

  // ── formatação ────────────────────────────────────────
  const fQtd=(v,u)=>{
    if(v==null) return '—';
    const nd=u==='und'?0:(u==='%'?2:1);
    return v.toLocaleString('pt-BR',{minimumFractionDigits:nd,maximumFractionDigits:nd})+' '+u;
  };
  const fTotal=v=>{ // v em R$ (bruto)
    if(v==null) return null;
    if(v>=1e9) return 'R$ '+(v/1e9).toFixed(2).replace('.',',')+' B';
    if(v>=1e6) return 'R$ '+(v/1e6).toFixed(2).replace('.',',')+' M';
    return 'R$ '+(v/1e3).toFixed(0)+' K';
  };
  const fCU=(cu,u)=>{
    if(cu==null) return null;
    // cu em R$ por unidade física
    let vStr;
    if(cu>=1e6) vStr='R$ '+(cu/1e6).toFixed(2).replace('.',',')+' M';
    else if(cu>=1e3) vStr='R$ '+(cu/1e3).toFixed(1).replace('.',',')+' K';
    else vStr='R$ '+cu.toFixed(0);
    return vStr+'/'+u;
  };

  // ── cálculo por KPI: retorna {qtd, total_rs, cu_rs, u} para cada lote ─
  const getKpiData=(tipo,lk)=>{
    switch(tipo){
      case 'extensao':{
        return {qtd:extensaoKm(lk),total:null,cu:null,u:'km'};
      }
      case 'capex':{
        const vMi=capexImpl(lk);
        const vRS=vMi*1e6;
        // total = unitário (conforme pedido)
        return {qtd:null,qtdFmt:vMi?fTotal(vRS):'—',total:vRS,cu:vRS,u:'—',mirrorTotal:true};
      }
      case 'capexkm':{
        const vMi=capexImpl(lk);
        const e=extensaoKm(lk);
        const cu=(vMi&&e)?(vMi*1e6)/e:null;
        return {qtd:cu?(cu/1e6):null,qtdFmt:cu?'R$ '+(cu/1e6).toFixed(2).replace('.',',')+' M/km':'—',total:null,cu:null,u:''};
      }
      case 'duplic':{
        const {total,cu}=cuRateado(lk,'1.2.1.1');
        return {qtd:cpQtd(lk,'1.2.1.1'),total,cu,u:'km'};
      }
      case 'contornos':{
        // soma qtd dos 2 contornos + total/CU médio ponderado
        const qs=cpQtd(lk,'1.2.1.2')||0;
        const qd=cpQtd(lk,'1.2.1.3')||0;
        const qtd=qs+qd;
        const r1=cuRateado(lk,'1.2.1.2');
        const r2=cuRateado(lk,'1.2.1.3');
        const total=(r1.total||0)+(r2.total||0)||null;
        const cu=(total&&qtd)?total/qtd:null;
        return {qtd:qtd||null,total,cu,u:'km'};
      }
      case 'marginais':{
        return {qtd:cpQtd(lk,'1.2.2'),total:cpVal(lk,'1.2.2'),cu:(cpQtd(lk,'1.2.2')&&cpVal(lk,'1.2.2'))?cpVal(lk,'1.2.2')/cpQtd(lk,'1.2.2'):null,u:'km'};
      }
      case 'faixas':{
        return {qtd:cpQtd(lk,'1.2.5'),total:cpVal(lk,'1.2.5'),cu:(cpQtd(lk,'1.2.5')&&cpVal(lk,'1.2.5'))?cpVal(lk,'1.2.5')/cpQtd(lk,'1.2.5'):null,u:'km'};
      }
      case 'ciclovias':{
        return {qtd:cpQtd(lk,'1.2.7'),total:cpVal(lk,'1.2.7'),cu:(cpQtd(lk,'1.2.7')&&cpVal(lk,'1.2.7'))?cpVal(lk,'1.2.7')/cpQtd(lk,'1.2.7'):null,u:'km'};
      }
      case 'intersec':{
        // Qtd total do grupo 1.2.3 + total do grupo + CU médio
        const subs=['1.2.3.2','1.2.3.3','1.2.3.4','1.2.3.5','1.2.3.6'];
        const qtd=subs.reduce((s,c)=>s+(cpQtd(lk,c)||0),0);
        const total=cpVal(lk,'1.2.3'); // total do grupo
        const cu=(total&&qtd)?total/qtd:null;
        return {qtd:qtd||null,total,cu,u:'und'};
      }
      case 'oaes':{
        // A9 (tipologia) traz valor em R$M e qtd
        const v=tipoVal(lk,'A9'); // R$ M
        const q=tipoQtd(lk,'A9');
        const total=v?v*1e6:null;
        const cu=(total&&q)?total/q:null;
        return {qtd:q,total,cu,u:'und'};
      }
      case 'passarelas':{
        return {qtd:cpQtd(lk,'1.2.10'),total:cpVal(lk,'1.2.10'),cu:(cpQtd(lk,'1.2.10')&&cpVal(lk,'1.2.10'))?cpVal(lk,'1.2.10')/cpQtd(lk,'1.2.10'):null,u:'und'};
      }
      default: return {qtd:null,total:null,cu:null,u:''};
    }
  };

  // ── definição dos KPIs (ordem de exibição) ───────────
  const KPIS=[
    {tipo:'extensao',   label:'Extensão total',     color:V.br2},
    {tipo:'capex',      label:'CAPEX Implantação',  color:V.or, highlight:true},
    {tipo:'capexkm',    label:'CAPEX / km',         color:V.or},
    {tipo:'duplic',     label:'Duplicação',         color:V.br},
    {tipo:'contornos',  label:'Contornos',          color:V.br},
    {tipo:'marginais',  label:'Marginais',          color:V.br},
    {tipo:'faixas',     label:'Faixas Adicionais',  color:V.br},
    {tipo:'ciclovias',  label:'Ciclovias',          color:V.br},
    {tipo:'intersec',   label:'Interseções',        color:V.br2},
    {tipo:'oaes',       label:'OAEs',               color:V.bl},
    {tipo:'passarelas', label:'Passarelas',         color:V.bl},
  ];

  return(<div style={{padding:'16px 20px',overflowY:'auto',flex:1}}>
    <div style={{marginBottom:14}}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:4}}>
        <div style={{fontSize:15,fontWeight:700,color:V.br}}><span style={{fontSize:11,fontFamily:'monospace',color:V.g4,marginRight:6,fontWeight:700}}>1.</span>Gestão de Orçamento</div>
        <span style={{padding:'3px 9px',background:V.blb,color:V.bl,fontSize:9.5,fontWeight:700,borderRadius:12,letterSpacing:'.04em'}}>ANTT</span>
        <span style={{padding:'3px 9px',background:V.g1,color:V.g5,fontSize:9.5,fontWeight:600,borderRadius:12}}>PR</span>
        <span style={{padding:'3px 9px',background:V.orp,color:V.or,fontSize:9.5,fontWeight:600,borderRadius:12}}>FEL-1</span>
      </div>
      <div style={{fontSize:11,color:V.g4}}>KPIs das obras de implantação · quantidade · valor total · valor unitário — Formação 01 · Concessão ANTT · Paraná · 3 lotes</div>
    </div>

    <div style={{background:'#fff',border:`1px solid ${V.g2}`,borderRadius:10,overflow:'hidden'}}>
      {/* Header dos lotes */}
      <div style={{display:'grid',gridTemplateColumns:`240px repeat(3,1fr)`,background:V.or,color:'#fff'}}>
        <div style={{padding:'10px 14px',fontSize:10.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em'}}>KPI</div>
        {lotes.map(lk=>{
          const d=DADOS[lk];
          return(<div key={lk} style={{padding:'10px 14px',fontSize:11,fontWeight:700,borderLeft:`1px solid rgba(255,255,255,.2)`}}>
            <div style={{fontSize:12,fontWeight:700}}>Lote {lk} · {d.sig}</div>
            <div style={{fontSize:9.5,opacity:.85,marginTop:2}}>{d.orgao} · {d.uf} · {d.fase} · {d.data_base}</div>
          </div>);
        })}
      </div>

      {/* Linhas de KPI (cada célula com qtd / total / cu empilhados) */}
      {KPIS.map((k,i)=>(
        <div key={i} style={{display:'grid',gridTemplateColumns:`240px repeat(3,1fr)`,borderTop:`1px solid ${V.g1}`,background:k.highlight?V.orp:(i%2===0?'#fff':V.g0)}}>
          <div style={{padding:'12px 14px',fontSize:11.5,fontWeight:k.highlight?700:600,color:k.highlight?V.br:V.g7,display:'flex',alignItems:'center',gap:6}}>
            {k.highlight&&<span style={{color:V.or,fontSize:10}}>●</span>}
            {k.label}
          </div>
          {lotes.map(lk=>{
            const d=getKpiData(k.tipo,lk);
            const qtdStr=d.qtdFmt!==undefined?d.qtdFmt:fQtd(d.qtd,d.u);
            const totalStr=d.total!=null?fTotal(d.total):null;
            const cuStr=d.cu!=null?(d.mirrorTotal?fTotal(d.cu):fCU(d.cu,d.u)):null;
            return(<div key={lk} style={{padding:'10px 14px',borderLeft:`1px solid ${V.g1}`,display:'flex',flexDirection:'column',gap:2,justifyContent:'center'}}>
              <div style={{fontSize:13,fontWeight:700,color:k.color,fontFamily:'monospace',lineHeight:1.2}}>
                {qtdStr}
              </div>
              {totalStr&&(
                <div style={{fontSize:10,fontWeight:500,color:V.g5,fontFamily:'monospace',lineHeight:1.3}}>
                  {totalStr}<span style={{fontSize:8.5,color:V.g4,marginLeft:4,fontWeight:400}}>total</span>
                </div>
              )}
              {cuStr&&(
                <div style={{fontSize:10,fontWeight:500,color:V.or,fontFamily:'monospace',lineHeight:1.3}}>
                  {cuStr}<span style={{fontSize:8.5,color:V.g4,marginLeft:4,fontWeight:400}}>unit.</span>
                </div>
              )}
            </div>);
          })}
        </div>
      ))}

      {/* BDI — linha única informativa no rodapé */}
      <div style={{display:'grid',gridTemplateColumns:`240px repeat(3,1fr)`,borderTop:`2px solid ${V.g2}`,background:V.g1}}>
        <div style={{padding:'9px 14px',fontSize:10.5,fontWeight:700,color:V.g5,display:'flex',alignItems:'center',gap:6}}>
          <span style={{color:V.g4,fontSize:9}}>◈</span>
          BDI Construção <span style={{color:V.g4,fontSize:9.5,marginLeft:'auto'}}>(referência)</span>
        </div>
        {lotes.map(lk=>{
          const bdi=DADOS[lk].bdi?.['Construção rodoviária'];
          return(<div key={lk} style={{padding:'9px 14px',borderLeft:`1px solid ${V.g2}`,display:'flex',alignItems:'center'}}>
            <div style={{fontSize:11.5,fontWeight:700,color:V.bl,fontFamily:'monospace'}}>
              {bdi!=null?bdi.toFixed(2).replace('.',',')+' %':'—'}
            </div>
          </div>);
        })}
      </div>
    </div>

    <div style={{marginTop:14,padding:'12px 14px',background:V.g1,border:`1px solid ${V.g2}`,borderRadius:8,fontSize:10.5,color:V.g5,lineHeight:1.6}}>
      <b style={{color:V.br}}>Escopo:</b> somente <b>obras de implantação</b> (CAPEX físico: duplicações, contornos, marginais, faixas adicionais, interseções, OAE, passarelas, acostamentos, ciclovias, correção de traçado). <b>Total</b> = valor PR Vias do item. <b>Unitário</b> = total ÷ quantidade (média ponderada para grupos). Exclui OPEX, receita, TIR/WACC, tarifa, capital social, equipamentos e edificações — disponíveis na aba <b>Orçamentação</b> por lote.
    </div>
  </div>);
});

export { GestaoPage };
export default GestaoPage;
